type SearchableOption = {
  label?: unknown;
};

type SearchableGroupedOption = SearchableOption & {
  options?: { label?: unknown }[];
};

enum GroupSearchRank {
  GroupLabelStartsWithInput = 0,
  GroupLabelContainsInput = 1,
  OptionLabelStartsWithInput = 2,
  OptionLabelContainsInput = 3,
  NoMatch = 4,
}

export const getGroupSearchRank = (option: SearchableGroupedOption, normalizedInput: string) => {
  if (!Array.isArray(option.options) || typeof option.label !== 'string') return GroupSearchRank.NoMatch;

  const groupLabel = option.label.toLocaleLowerCase();
  if (groupLabel.startsWith(normalizedInput)) return GroupSearchRank.GroupLabelStartsWithInput;
  if (groupLabel.includes(normalizedInput)) return GroupSearchRank.GroupLabelContainsInput;

  const optionLabels = option.options.flatMap((childOption) =>
    typeof childOption.label === 'string' ? [childOption.label.toLocaleLowerCase()] : [],
  );

  if (optionLabels.some((label) => label.startsWith(normalizedInput))) {
    return GroupSearchRank.OptionLabelStartsWithInput;
  }
  if (optionLabels.some((label) => label.includes(normalizedInput))) {
    return GroupSearchRank.OptionLabelContainsInput;
  }
  return GroupSearchRank.NoMatch;
};

export const prioritizeGroupedOptions = <T extends SearchableGroupedOption>(
  options: T[],
  normalizedInput: string,
): T[] => {
  const rankedOptions = [...options].sort(
    (optionA, optionB) => getGroupSearchRank(optionA, normalizedInput) - getGroupSearchRank(optionB, normalizedInput),
  );

  // New child references make react-select focus the first result instead of retaining a stale highlighted option.
  return rankedOptions.map((option) => ({
    ...option,
    ...(Array.isArray(option.options) && {
      options: option.options.map((childOption) => ({ ...childOption })),
    }),
  })) as T[];
};
